import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";

import FormField from "@mhj/shared/FormField";
import { apiRequest } from "@mhj/shared/api";
import type { CuriousQuestion } from "@mhj/shared/types";

export default function AdminCurious() {
  const queryClient = useQueryClient();
  const [questions, setQuestions] = useState<CuriousQuestion[]>([]);
  const [selected, setSelected] = useState<CuriousQuestion | null>(null);
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<{ answer: string }>({
    defaultValues: { answer: "" },
  });
  const { data, error } = useQuery({
    queryKey: ["admin", "curious"],
    queryFn: () => apiRequest<{ questions: CuriousQuestion[] }>("/admin/curious"),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });

  useEffect(() => {
    if (data?.questions) {
      setQuestions(data.questions);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      toast.error((error as Error).message);
    }
  }, [error]);

  const statusMutation = useMutation({
    mutationFn: (payload: { id: string; action: "approve" | "reject" }) =>
      apiRequest(`/admin/curious/${payload.id}/${payload.action}`, {
        method: "POST",
        withCsrf: true,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "curious"] });
      setSelected(null);
      reset({ answer: "" });
      toast.success("Question status updated.");
    },
  });

  const answerMutation = useMutation({
    mutationFn: (payload: { id: string; answer: string }) =>
      apiRequest(`/admin/curious/${payload.id}/answer`, {
        method: "POST",
        data: { answer: payload.answer },
        withCsrf: true,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "curious"] });
      setSelected(null);
      reset({ answer: "" });
      toast.success("Answer saved.");
    },
  });

  const handleSelect = (question: CuriousQuestion) => {
    setSelected(question);
    setValue("answer", question.answer ?? "");
  };

  const handleStatus = async (next: "approve" | "reject") => {
    if (!selected) return;
    try {
      await statusMutation.mutateAsync({ id: selected.id, action: next });
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleAnswer = async (values: { answer: string }) => {
    if (!selected) return;
    try {
      await answerMutation.mutateAsync({ id: selected.id, answer: values.answer });
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_1.1fr]">
      <div className="glass-card rounded-3xl p-6">
        <p className="text-xs uppercase tracking-[0.3em] text-blue-300">
          Curious Cat
        </p>
        <h2 className="mt-3 text-2xl font-semibold text-black">
          Moderation queue
        </h2>
        <div className="mt-6 space-y-3 text-sm text-black/70">
          {questions.length === 0 ? (
            <p>No questions yet.</p>
          ) : (
            questions.map((question) => (
              <button
                key={question.id}
                onClick={() => handleSelect(question)}
                className="w-full rounded-2xl border border-black/10 bg-white/60 px-4 py-3 text-left transition hover:border-blue-400"
              >
                <p className="font-semibold text-black">{question.question}</p>
                <p className="text-xs text-black/50">
                  {question.name} · {question.status}
                </p>
              </button>
            ))
          )}
        </div>
      </div>
      <div className="glass-card rounded-3xl p-6">
        <p className="text-xs uppercase tracking-[0.3em] text-blue-300">
          Question detail
        </p>
        {selected ? (
          <>
            <h3 className="mt-3 text-xl font-semibold text-black">
              {selected.question}
            </h3>
            <p className="mt-2 text-xs text-black/50">
              {selected.name} · {selected.email}
            </p>
            <p className="mt-4 text-sm text-black/70">
              Status: {selected.status}
            </p>
            <div className="mt-6">
              <FormField label="Answer (published)">
                <textarea
                  {...register("answer", {
                    required: "Answer is required",
                    minLength: { value: 3, message: "Answer is too short" },
                  })}
                  rows={5}
                  className="rounded-2xl border border-black/10 bg-white/60 px-4 py-3 text-black focus:border-blue-400 focus:outline-none"
                />
              </FormField>
              {errors.answer ? (
                <p className="mt-2 text-xs text-red-500">{errors.answer.message}</p>
              ) : null}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                onClick={() => void handleStatus("approve")}
                disabled={statusMutation.isPending}
                className="rounded-full bg-emerald-500/80 px-5 py-2 text-xs uppercase tracking-[0.2em] text-white transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Approve
              </button>
              <button
                onClick={() => void handleStatus("reject")}
                disabled={statusMutation.isPending}
                className="rounded-full bg-red-500/80 px-5 py-2 text-xs uppercase tracking-[0.2em] text-white transition hover:bg-red-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Reject
              </button>
              <button
                onClick={handleSubmit(handleAnswer)}
                disabled={answerMutation.isPending}
                className="rounded-full border border-black/20 px-5 py-2 text-xs uppercase tracking-[0.2em] text-black/70 transition hover:border-blue-400 hover:text-blue-200 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Save answer
              </button>
            </div>
          </>
        ) : (
          <p className="mt-4 text-sm text-black/60">
            Select a question to moderate.
          </p>
        )}
      </div>
    </div>
  );
}
